import { useState } from 'react';
import type { SettlementHistoryItem } from '@fdh/shared';
import { useLanguage } from '../../../contexts/LanguageContext';
import {
  formatEmployerHistorySettlementResult,
  formatEmployerMoneyAmount,
  resolveEmployerHistoryStatusClass,
  resolveEmployerHistoryStatusLabel,
} from '../../../utils/employerMoneyGivenSettlement';
import { SettlementHistoryDetailModal } from './SettlementHistoryDetailModal';

type SettlementHistorySectionProps = {
  title: string;
  items: SettlementHistoryItem[];
  submittedAtLabel: string;
  eventAtLabel: string;
  moneyGivenAmountLabel: string;
  totalSpentLabel: string;
  settlementResultLabel: string;
  rejectReasonLabel: string;
  receiptsTitle: string;
  viewDetailAria: string;
  closeLabel: string;
};

export const SettlementHistorySection = ({
  title,
  items,
  submittedAtLabel,
  eventAtLabel,
  moneyGivenAmountLabel,
  totalSpentLabel,
  settlementResultLabel,
  rejectReasonLabel,
  receiptsTitle,
  viewDetailAria,
  closeLabel,
}: SettlementHistorySectionProps) => {
  const { t } = useLanguage();
  const [selectedItem, setSelectedItem] = useState<SettlementHistoryItem | null>(null);

  if (items.length === 0) {
    return null;
  }

  const handleOpenItem = (item: SettlementHistoryItem) => {
    setSelectedItem(item);
  };

  const detailButtonClass =
    'mt-3 flex min-h-10 w-full items-center justify-center rounded-lg border-2 border-primary-600 bg-white py-2 text-sm font-semibold text-primary-600 transition-colors hover:bg-primary-600/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 dark:border-emerald-500 dark:bg-gray-900 dark:text-emerald-300 dark:hover:bg-emerald-950/40';

  return (
    <>
      <section aria-labelledby="settlement-history-heading">
        <h2 id="settlement-history-heading" className="text-base font-semibold text-gray-900 dark:text-gray-100">
          {title}
        </h2>
        <ul className="mt-2 space-y-2" role="list">
          {items.map((item) => {
            const balanceClass =
              item.balance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400';
            const statusLabel = resolveEmployerHistoryStatusLabel(item.status, t);
            const statusClass = resolveEmployerHistoryStatusClass(item.status);
            return (
              <li key={item.id}>
                <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className={`text-sm font-semibold ${statusClass}`}>{statusLabel}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{item.eventAt}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">{totalSpentLabel}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {formatEmployerMoneyAmount(item.totalSpent)}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">{moneyGivenAmountLabel}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {formatEmployerMoneyAmount(item.budgetAmount)}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">{settlementResultLabel}</span>
                    <span className={`font-semibold ${balanceClass}`}>
                      {formatEmployerHistorySettlementResult(item.balance, t)}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleOpenItem(item)}
                    className={detailButtonClass}
                    tabIndex={0}
                    aria-label={viewDetailAria}
                  >
                    {t('settlement.historyDetailButton')}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </section>
      {selectedItem ? (
        <SettlementHistoryDetailModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          submittedAtLabel={submittedAtLabel}
          eventAtLabel={eventAtLabel}
          moneyGivenAmountLabel={moneyGivenAmountLabel}
          totalSpentLabel={totalSpentLabel}
          settlementResultLabel={settlementResultLabel}
          rejectReasonLabel={rejectReasonLabel}
          receiptsTitle={receiptsTitle}
          closeLabel={closeLabel}
        />
      ) : null}
    </>
  );
};
